import 'video.js/dist/video-js.css';
import './styles.scss';
import React, { useEffect, useRef, useState } from 'react';
import { GeckoBtn } from '@geckolabs/gecko-react-ui';
import StreamService from '../../services/StreamService';
import EventService from '../../services/EventService';
import VideoService from '../../services/VideoService';
import DateUtils from '../../../../services/DateUtils';
import {__, STR_KEY} from '../../../../services/Translate';
import { ResponsiveService } from '../../services/ResponsiveService';
import VideoBuffering from './VideoBuffering';
import {
    MESSAGE_HIGHLIGHTED_EVENT,
    HOST_HIGHLIGHTED_EVENT,
    VIDEO_EMOJI_SMILE_EVENT,
    VIDEO_EMOJI_HEART_EVENT,
    VIDEO_EMOJI_THUMBS_UP_EVENT,
} from '../../constants';
import { Listener, UnbindListener } from '../../services/_';
import VideoHighlightService from '../../services/VideoHighlightService';
import VideoReactionsService from '../../services/VideoReactionsService';
import { addEmojiButtons } from './Reactions';


const renderEventStartDateTime = (event) => {
    const fn = DateUtils.isSameDay(event.start_datetime, DateUtils.now()) ? 'hourMinute' : 'dayMonthYearHourMinute';
    return DateUtils[fn](event.start_datetime);
};

const Video = ({stream, event}) => {
    const [isIdle, setIsIdle] = useState(true);

    useEffect(() => {
        // Dont interupt video when playing
        if (StreamService.isIdle(stream.status) && VideoService.player()) {
            
        // Video has ended
            if (VideoService.currentFragmentId() === VideoService.lastFragmentId()) {
                setIsIdle(true);
        // Video is still playing fallback
            } else {
                VideoService.player().one(['canplaythrough', 'ended'], () => setIsIdle(true));
            }

            return;
        }

        setIsIdle(StreamService.isIdle(stream.status));

    }, [stream]);

    let component;

    if (isIdle) {
        const header = !EventService.hasStarted(event) ? __(STR_KEY.VIDEO_PLAYER_IDLE_HEADER_NOT_STARTED) : __(STR_KEY.VIDEO_PLAYER_IDLE_HEADER_STARTED);
        const description = !EventService.hasStarted(event) ? `${__(STR_KEY.VIDEO_PLAYER_IDLE_DESCRIPTION_NOT_STARTED)} ${renderEventStartDateTime(event)}.` : null;
        component = (<VideoFallbackWrapper header={header} description={description} />);
    } else {
        component = (<VideoPlayer viewUrl={stream.view_url} />);
    }

    return (            
        <div className="video-container d-flex col">
            <div className="video d-flex align-items-stretch">
                {component}
            </div>
        </div>
    );
};

const VideoFallbackWrapper = ({header, description, showAnimation}) => {
    return (
        <div className="video-fallback-wrapper">
            <div className="embed-responsive embed-responsive-16by9">
                <div className="video-fallback-message">
                    {header ? (<h1>{header}</h1>) : null}
                    {description ? (<div>{description}</div>) : null}
                </div>
            </div>
        </div>
    );
};

const VideoPermissionOverlay = ({handler}) => {
    return (
        <div className="video-permissions-wrapper">
            <p>Play sound?</p>
            <div>
                <GeckoBtn name={__(STR_KEY.VIDEO_PLAYER_PERMISSION_BTN_YES)} id="sound-yes" handler={handler.bind(this, true)} />
                <GeckoBtn name={__(STR_KEY.VIDEO_PLAYER_PERMISSION_BTN_NO)} id="sound-no"  handler={handler.bind(this, false)} />
            </div>
        </div>
    );
};

// Apple Device Fix (Play again after exiting fullscreen and picture in picture view)
// Ref: https://css-tricks.com/what-does-playsinline-mean-in-web-video/
const appleDeviceFullScreenFix = () => {
    var isAppleDevice = navigator.userAgent.match(/Mac/) && navigator.userAgent.match(/Safari/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2; 
    if (player && isAppleDevice) {
        player.on('fullscreenchange', () => {
            if (!player.isFullscreen() && player.paused()) {
                setTimeout(() => player.play(), 500);
            }
        });
    }
};

let player;

const VideoPlayer = ({viewUrl}) => {
    const playerRef                                 = useRef();
    const [showLoading, setShowLoading]             = useState(true);
    const [showPermissions, setShowPermissions]     = useState(false);

    useEffect(() => {

        if (!playerRef.current) return;

        // Init VideoJS
        player = VideoService.init(playerRef.current);

        if (!player) return;

    // TEMP: For Debugging Purposes
        console.log('INIT');

        player.on('ready', () => {
            console.log('READY');
        });

        player.on('playing', () => {
            console.log('PLAYING');
        });

        player.on('waiting', () => {
            console.log('WAITING');
        });

        player.on('error', (err) => {
            console.log('ERROR', err);
        });
    // /TEMP: For Debugging Purposes

        player.on('loadeddata', () => {
            
            console.log('LOADEDDATA');

            // Remove loading view when video plays (for the first time)
            player.one('playing', () => {
                console.log('REMOVE LOADING VIEW');
                setShowLoading(false);
                // Add Emoji button bar
                addEmojiButtons();
            });

            // Play the video
            player.one('canplay', () => {

                console.log('CANPLAY');
                
                // Unmute audio and (attempt to) play video
                player.muted(false);
            
                player.play().catch(err => {
                    // When unmuting fails; show permsions overlay
                    player.one('play', () => setShowPermissions(true));

                    // When unmuting fails; re-mute audio and play video
                    player.muted(true);
                    player.play().catch(err => {
                        // Further fallback to allow manual video play
                        setShowLoading(false);
                    });
                });
                
            });

        });

        // iPhone Fix (Play again after exiting fullscreen and picture in picture view)
        appleDeviceFullScreenFix();  

        // Destroy
        return () => { player = VideoService.destroy() };
    }, []);

    useEffect(() => {

        if (!player || !viewUrl) return;

        // Show loading view while source is being loaded
        setShowLoading(true);

        // Load video in player
        player.src({
            src: viewUrl,
            type: 'application/x-mpegURL',
            withCredentials: false,
        });
                
    }, [viewUrl]);    

    // Video Highlights
    useEffect(() => {
        const listeners = [
            Listener(MESSAGE_HIGHLIGHTED_EVENT, VideoHighlightService.message),
            Listener(HOST_HIGHLIGHTED_EVENT, VideoHighlightService.host),
            Listener(VIDEO_EMOJI_HEART_EVENT, VideoReactionsService.heart),
            Listener(VIDEO_EMOJI_SMILE_EVENT, VideoReactionsService.smile),
            Listener(VIDEO_EMOJI_THUMBS_UP_EVENT, VideoReactionsService.thumbsUp),
        ];

        return () => {
            VideoHighlightService.destroy();
            VideoReactionsService.destroy();
            UnbindListener(listeners);
        }
    }, [])

    useEffect(() => {
        // ADD css class to body tag when video is PLAYING
        ResponsiveService.video.isPlaying(true);
        // REMOVE css class from body tag when video is NOT playing
        return () => ResponsiveService.video.isPlaying(false);
    }, []);

    const soundFn = (allow) => {
        if (player) {
            player.muted(allow ? false : true);
            setShowPermissions(false);
        }        
    };

    return (
        <>
            <div className="embed-responsive embed-responsive-16by9">
                <video 
                    id="video-container"
                    className="video-js vjs-theme-fantasy"
                    ref={playerRef}
                    controls
                    muted
                    autoPlay
                    disablePictureInPicture
                    playsInline
                />
                {showLoading ? (<VideoBuffering />) : null}
                {showPermissions ? <VideoPermissionOverlay handler={soundFn.bind(this)} /> : null}
            </div>
        </>
    );
};

export default Video;